/**
 * SIL-1647: bench agent <-> API wire contract.
 *
 * Every shape the Rust agent (apps/bench-agent, serde camelCase) and the API
 * exchange is defined here and nowhere else. The fixtures in
 * apps/bench-agent/fixtures/contract/ are parsed by both sides; see
 * __tests__/contract-fixtures.test.ts.
 *
 * Strings that originate on the device (serials, models, vendors) are shown to
 * a portal user before they approve a pairing, so they are printable ASCII
 * only: no control characters and no bidirectional overrides that could make
 * one serial look like another.
 */
import { z } from 'zod';

const asciiText = (max: number) =>
  z
    .string()
    .max(max)
    .regex(/^[\x20-\x7e]*$/);

export const benchDeviceKindSchema = z.enum(['chassis', 'gpu', 'nvme', 'dimm', 'cpu', 'psu']);

export const benchDeviceSchema = z.object({
  deviceIndex: z.number().int().nonnegative(),
  // NVMe: `${serial}|${model}|${eui64OrNguid}`; others: `${kind}:${serial ?? pciAddress ?? index}`
  deviceKey: asciiText(256).min(1),
  kind: benchDeviceKindSchema,
  parentDeviceIndex: z.number().int().nonnegative().nullable(),
  pciId: z
    .string()
    .regex(/^[0-9a-f]{4}:[0-9a-f]{4}:[0-9a-f]{4}:[0-9a-f]{4}$/)
    .nullable(),
  pciAddress: asciiText(32).nullable(),
  pcieGen: z.number().int().nullable(),
  pcieWidth: z.number().int().nullable(),
  vendor: asciiText(128).nullable(),
  model: asciiText(128).nullable(),
  serial: asciiText(128).nullable(),
  firmware: asciiText(64).nullable(),
  nvme: z
    .object({
      eui64: asciiText(64).nullable(),
      nguid: asciiText(64).nullable(),
      ieeeOui: asciiText(64).nullable(),
      percentageUsed: z.number().int().min(0).max(255).nullable(),
      mediaErrors: z.number().int().nonnegative().nullable(),
    })
    .nullable(),
  readErrors: z.array(z.object({ field: asciiText(64), reason: asciiText(256) })),
});

export const benchChassisSchema = z.object({
  sysVendor: asciiText(128).nullable(),
  productName: asciiText(128).nullable(),
  productSerial: asciiText(128).nullable(),
  chassisSerial: asciiText(128).nullable(),
  boardSerial: asciiText(128).nullable(),
  // Keys and values are device-originated and shown to the approver: printable
  // ASCII only, and at most 64 entries so one pair/start cannot bloat device_summary.
  fru: z
    .record(asciiText(128), asciiText(128))
    .refine((r) => Object.keys(r).length <= 64, { message: 'fru has more than 64 keys' })
    .nullable(),
});

/** The discovery shape without the duplicate-key rule: for reading stored discoveries, which must still render. */
export const benchDiscoveryShapeSchema = z.object({
  schemaVersion: z.literal(1),
  chassis: benchChassisSchema,
  devices: z.array(benchDeviceSchema).max(512),
});

export const benchDiscoverySchema = benchDiscoveryShapeSchema.superRefine((d, ctx) => {
  // A shared key would let one wipe authorization name several drives.
  const seen = new Set<string>();
  d.devices.forEach((dev, i) => {
    if (seen.has(dev.deviceKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['devices', i, 'deviceKey'],
        message: 'duplicate deviceKey',
      });
    }
    seen.add(dev.deviceKey);
  });
});

export const benchPairStartResponseSchema = z.object({
  deviceCode: z.string(),
  userCode: z.string(),
  verificationUri: z.string().url(),
  verificationUriComplete: z.string().url(),
  interval: z.number().int(),
  expiresIn: z.number().int(),
});

export const benchPairPollRequestSchema = z.object({ deviceCode: z.string() });

export const benchAttestationLevelSchema = z.enum(['registered_bench', 'self_reported']);

/**
 * SIL-1671 E-09: multi-environment runs. Where a stage runs and who can attest
 * to it. Everything here is optional on the wire so a deployed API and an
 * older agent keep working; an absent `environment` means `linux`.
 */
export const benchEnvironmentSchema = z.enum(['linux', 'bmc', 'uefi', 'winpe']);
/** The environment of an agent that can present a resume token. */
export const benchAgentEnvironmentSchema = z.enum(['linux', 'winpe']);
/** A tokenless foreign image the machine visits before the returning agent shows up. */
export const benchForeignVisitSchema = z.literal('uefi');
/** How a `uefi` stage's result comes back (spec 7.2). */
export const benchCollectSchema = z.enum(['bmc_log', 'vendor_export', 'operator']);
/**
 * Who observed the result. Assigned by the API from the pinned profile: it is
 * carried by profile stages (server -> agent) and stored rows, and is
 * deliberately absent from every request body an agent or portal user sends.
 * Anything other than `agent_observed` is negative-only for grading.
 */
export const benchEvidenceClassSchema = z.enum([
  'agent_observed',
  'bmc_reported',
  'operator_attested',
]);

export type BenchEnvironment = z.infer<typeof benchEnvironmentSchema>;
export type BenchAgentEnvironment = z.infer<typeof benchAgentEnvironmentSchema>;
export type BenchCollect = z.infer<typeof benchCollectSchema>;
export type BenchEvidenceClass = z.infer<typeof benchEvidenceClassSchema>;

/** Stage environments each agent kind executes (spec 3.2). `uefi` has no agent. */
export const BENCH_AGENT_CAPABILITIES: Record<BenchAgentEnvironment, readonly BenchEnvironment[]> =
  {
    linux: ['linux', 'bmc'],
    winpe: ['winpe'],
  };

export function benchAgentCanRun(
  agent: BenchAgentEnvironment,
  stage: BenchEnvironment | undefined,
): boolean {
  return BENCH_AGENT_CAPABILITIES[agent].includes(stage ?? 'linux');
}

export const benchPairStartRequestSchema = z.object({
  discovery: benchDiscoverySchema,
  primaryMac: z.string().regex(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/),
  /**
   * Environment of the booting agent; absent means `linux`. The API refuses a
   * non-attach approval when this is `winpe` (spec 8.1, T20): a winpe agent can
   * only continue an existing run. Enforcement belongs to the API ticket; the
   * field lands here.
   */
  environment: benchAgentEnvironmentSchema.optional(),
});

/** Opaque URL-safe secret (32 random bytes encode to 43 chars). */
const opaqueToken = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/);
/**
 * Stable name of one stage inside a pinned plan; opaque to the agent. The
 * format is `${profileId}:${stageIndex}` (a profile-stage reference, not a
 * per-device completion: see `benchCompletedStageSchema`).
 */
export const benchStageKeySchema = asciiText(256).min(1);
const MAX_SEGMENTS = 8;
const MAX_STAGE_KEYS = 512;
const MAX_COMPLETED_STAGES = 4096;

/**
 * One visit of the run. Step i is segment i: step 0 is the paired Linux boot,
 * so a foreign visit (`uefi`) or a `winpe` agent can only appear from step 1.
 */
export const benchPlanStepSchema = z
  .object({
    /** Environment of the agent that presents the resume token for this visit. */
    environment: benchAgentEnvironmentSchema,
    foreignVisit: benchForeignVisitSchema.optional(),
    /**
     * Boot image of this visit (the foreign image when `foreignVisit` is set,
     * else the winpe image). The API needs it per step to refuse `internal_only`
     * images at plan time (spec 7.4). Required exactly when the step boots a
     * non-default image.
     */
    imageId: asciiText(128).min(1).optional(),
    stageKeys: z.array(benchStageKeySchema).min(1).max(MAX_STAGE_KEYS),
  })
  .superRefine((step, ctx) => {
    const needsImage = step.foreignVisit !== undefined || step.environment === 'winpe';
    if (needsImage && step.imageId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imageId'],
        message: 'imageId is required for a foreignVisit or winpe step',
      });
    }
    if (!needsImage && step.imageId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imageId'],
        message: 'imageId is only allowed on a foreignVisit or winpe step',
      });
    }
  });

/** Run-level and write-once. Carries no evidence class: that comes from the pinned profile. */
export const benchPlanSchema = z
  .object({
    profilePins: z
      .array(
        z.object({
          profileId: asciiText(128).min(1),
          version: z.number().int().positive().max(2147483647),
        }),
      )
      .min(1)
      .max(64),
    steps: z.array(benchPlanStepSchema).min(1).max(MAX_SEGMENTS),
  })
  .superRefine((plan, ctx) => {
    const seen = new Set<string>();
    plan.profilePins.forEach((p, i) => {
      if (seen.has(p.profileId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['profilePins', i, 'profileId'],
          message: 'duplicate profileId',
        });
      }
      seen.add(p.profileId);
    });
    const first = plan.steps[0];
    if (first && (first.environment !== 'linux' || first.foreignVisit !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', 0],
        message: 'step 0 is the paired linux boot: linux, no foreignVisit',
      });
    }
    const last = plan.steps[plan.steps.length - 1];
    if (last && last.environment !== 'linux') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', plan.steps.length - 1, 'environment'],
        message: 'the last step must be linux (the run commits from linux)',
      });
    }
  });

export const benchPlanRequestSchema = benchPlanSchema;
export const benchPlanResponseSchema = z.object({ plan: benchPlanSchema });

/** The per-device completion of one profile stage (spec 5.5 natural key, minus segment). */
export const benchCompletedStageSchema = z
  .object({
    deviceKey: asciiText(256).min(1),
    profileId: asciiText(128).min(1),
    stageIndex: z.number().int().min(0).max(2147483647),
    environment: benchEnvironmentSchema,
  })
  .strict();

export const benchRunStateSchema = z.object({
  runId: z.string().uuid(),
  status: z.enum(['running', 'committed', 'abandoned']),
  wipeAuthorizedDeviceKeys: z.array(z.string()),
  // Latest action for the device is 'revoke': the agent skips its destructive
  // stage (errorReason 'wipe_declined') instead of waiting forever.
  wipeDeclinedDeviceKeys: z.array(z.string()),
});

/**
 * Where a continuing agent picks the run up. Shared by an approved poll that
 * attached this boot to an open run (spec 5.4) and by the resume response.
 */
export const benchRunContinuationSchema = z.object({
  segmentIdx: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_SEGMENTS - 1),
  nextSeq: z.number().int().nonnegative().max(2147483647),
  sessionExpiresAt: z.string().datetime(),
  plan: benchPlanSchema.nullable(),
  completedStageKeys: z.array(benchCompletedStageSchema).max(MAX_COMPLETED_STAGES),
  runState: benchRunStateSchema,
});

export const benchPairPollResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('authorization_pending') }),
  z.object({ status: z.literal('slow_down') }),
  z.object({ status: z.literal('access_denied') }),
  z.object({ status: z.literal('expired_token') }),
  z.object({
    status: z.literal('approved'),
    sessionToken: z.string(),
    runId: z.string().uuid(),
    wipeCode: z.string().regex(/^\d{6}$/),
    approvedByEmail: z.string(),
    orgName: z.string(),
    attestationLevel: benchAttestationLevelSchema,
    // Present when the approver attached this boot to an open run instead of
    // starting a new one; `runId` is then that run's id.
    attach: benchRunContinuationSchema.optional(),
  }),
]);

export const benchStageStatusSchema = z.enum(['pass', 'fail', 'timeout', 'error', 'skipped']);

export const benchProfileStageSchema = z
  .object({
    stageIndex: z.number().int(),
    name: z.string(),
    executable: z.string(),
    arguments: z.array(z.string()),
    parseFormat: z.enum(['DCGM_JSON', 'NVME_SMART_JSON', 'SMARTCTL_JSON', 'NVME_FORMAT_TEXT']),
    fatalOnFailure: z.boolean(),
    destructive: z.boolean(),
    timeoutSeconds: z.number().int().positive(),
    // SIL-1671: absent means `linux` / agent_observed / no collect.
    environment: benchEnvironmentSchema.optional(),
    collect: benchCollectSchema.optional(),
    evidenceClass: benchEvidenceClassSchema.optional(),
  })
  .superRefine((s, ctx) => {
    const env = s.environment ?? 'linux';
    const cls = s.evidenceClass;
    const bad = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const agentRuns = env === 'linux' || env === 'winpe';
    if (s.destructive && env !== 'linux') bad('environment', 'a destructive stage runs in linux');
    // Stricter than the DB CHECK on purpose: the class follows who observed it
    // (spec 3.3). An in-band BMC read (racadm/ipmitool from Linux) is modelled as
    // `environment: 'bmc'`, not as a linux stage with a BMC class.
    if (agentRuns && cls !== undefined && cls !== 'agent_observed') {
      bad('evidenceClass', `a ${env} stage is agent_observed`);
    }
    if (!agentRuns && (cls === undefined || cls === 'agent_observed')) {
      bad('evidenceClass', `a ${env} stage is bmc_reported or operator_attested`);
    }
    if (env === 'uefi' && s.collect === undefined) bad('collect', 'a uefi stage needs collect');
    if (env !== 'uefi' && s.collect !== undefined) bad('collect', 'collect is only for uefi');
    if (s.collect === 'operator' && cls !== 'operator_attested') {
      bad('evidenceClass', 'collect operator is operator_attested');
    }
    if (s.collect === 'bmc_log' && cls !== 'bmc_reported') {
      bad('evidenceClass', 'collect bmc_log is bmc_reported');
    }
  });

type StageEnvFields = Pick<
  z.infer<typeof benchProfileStageSchema>,
  'environment' | 'collect' | 'evidenceClass'
>;

/** The environment a stage runs in (absent means linux). */
export function benchStageEnvironment(
  stage: Pick<StageEnvFields, 'environment'>,
): BenchEnvironment {
  return stage.environment ?? 'linux';
}

/**
 * The evidence class of a stage. An absent class is `agent_observed` only where
 * that is legal (linux, winpe); a bmc/uefi stage with no class is invalid and
 * yields null (the schema rejects it).
 */
export function benchStageEvidenceClass(stage: StageEnvFields): BenchEvidenceClass | null {
  if (stage.evidenceClass !== undefined) return stage.evidenceClass;
  const env = benchStageEnvironment(stage);
  return env === 'linux' || env === 'winpe' ? 'agent_observed' : null;
}

export const benchProfileSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  hardwareMatch: z.object({
    kind: benchDeviceKindSchema,
    pciVendorId: z.string().optional(),
    pciDeviceId: z.string().optional(),
  }),
  executionPolicy: z.object({
    concurrencyGroup: z.string(),
    allowParallelExecution: z.boolean(),
  }),
  stages: z.array(benchProfileStageSchema),
});

// Every column these land in is int4 or text; an out-of-range number or a NUL
// in a string would raise in Postgres and surface as a 500, so the contract
// bounds them (clean 422 instead). `summary` is unbounded JSON; the stages
// route additionally refuses a NUL escape anywhere in it (jsonb rejects it).
const int32 = z.number().int().min(-2147483648).max(2147483647);

export const benchStageResultSchema = z.object({
  // SIL-1683: advisory when stageIndex is present - the API allocates the
  // stored seq and returns it in the ack. Authoritative only for a legacy post
  // (no stageIndex).
  seq: int32.min(0),
  // ProfileStage.stageIndex. With (run, deviceKey, profileId) it is the stage's
  // natural key; its presence selects the idempotent server-seq path.
  stageIndex: int32.min(0).optional(),
  // SIL-1705 (E-19): the run segment the agent believes it is posting into
  // (0 for the paired boot, the attach/resume segment otherwise). ADVISORY:
  // the API stores its own active segment and refuses a mismatch (409
  // wrong_segment). REQUIRED when the run has segment rows (every run approved
  // since E-11); optional only for a legacy run with none. Ignored on an
  // un-keyed (no stageIndex) post. Excluded from the idempotency hash, so a
  // retry of stored bytes is a duplicate whatever segment it names.
  segmentIdx: int32.min(0).max(15).optional(),
  deviceIndex: int32.min(0),
  deviceKey: asciiText(256).min(1),
  profileId: asciiText(128),
  stageName: asciiText(128),
  destructive: z.boolean(),
  fatalOnFailure: z.boolean(),
  wipeTicketId: z.string().uuid().nullable(),
  status: benchStageStatusSchema,
  exitCode: int32.nullable(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
  summary: z.record(z.unknown()),
  outputTruncated: z.boolean(),
  errorReason: asciiText(256).nullable(),
});

export const benchStageAckSchema = z.object({
  seq: z.number().int(),
  result: z.enum(['created', 'duplicate']),
});

export const benchWipeStartRequestSchema = z.object({ deviceKey: asciiText(256).min(1) });
export const benchWipeStartResponseSchema = z.object({ wipeTicketId: z.string().uuid() });

// A SHA-256 digest in the S3 checksum header format: 32 bytes, standard base64.
const sha256Base64 = z.string().regex(/^[A-Za-z0-9+/]{43}=$/);

export const benchEvidenceUrlRequestSchema = z.object({
  sha256Base64,
  sizeBytes: z.number().int().positive().max(2_147_483_648),
});
export const benchEvidenceUrlResponseSchema = z.object({
  url: z.string().url(),
  headers: z.record(z.string()),
});

export const benchHandoffRequestSchema = z.object({
  resumeEnvironment: benchAgentEnvironmentSchema,
  foreignVisit: benchForeignVisitSchema.optional(),
  segmentSha256Base64: sha256Base64,
  segmentSize: z.number().int().positive().max(2_147_483_648),
});

/**
 * What the PXE/iPXE side may see. No secret, no run id, no resume token: the
 * boot fetch travels the LAN. `bootId` is 128-bit random, single use, and
 * expires with the armed window.
 */
export const benchBootTargetSchema = z.object({
  imageId: asciiText(128).min(1),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bootId: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/),
});

/** The resume secret. Goes only into UEFI NVRAM, never into a boot URL or PXE script. */
export const benchResumeGrantSchema = z.object({
  token: opaqueToken,
  expiresAt: z.string().datetime(),
});

export const benchHandoffResponseSchema = z.object({
  /** Non-null iff the next plan step has a foreignVisit or environment winpe. */
  bootTarget: benchBootTargetSchema.nullable(),
  resume: benchResumeGrantSchema,
});

export const benchResumeRequestSchema = z.object({
  runId: z.string().uuid(),
  resumeToken: opaqueToken,
  environment: benchAgentEnvironmentSchema,
  deviceSummary: z.object({
    chassisSerial: asciiText(128).nullable().optional(),
    boardSerial: asciiText(128).nullable().optional(),
    // SIL-1704 (O1): identity is stored product > chassis > board and matched by set membership; without this field a
    // run identified by its product serial burns its token on a legitimate resume. zod strips unknown keys, so an
    // agent that sends it today would have it silently dropped.
    productSerial: asciiText(128).nullable().optional(),
    primaryMac: z.string().regex(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/),
    nvmeDeviceKeys: z.array(asciiText(256).min(1)).max(512).optional(),
  }),
  agentVersion: asciiText(64).min(1),
  imageId: asciiText(128).min(1),
});

export const benchResumeResponseSchema = z
  .object({
    sessionToken: opaqueToken,
    wipeCode: z.string().regex(/^\d{6}$/),
    approvedByEmail: z.string(),
    orgName: z.string(),
    attestationLevel: benchAttestationLevelSchema,
  })
  .merge(benchRunContinuationSchema.extend({ plan: benchPlanSchema }));

export const benchCommitRequestSchema = z.object({
  discovery: benchDiscoverySchema,
  stageSeqs: z.array(z.number().int()),
  evidenceSha256Base64: sha256Base64,
});

export const benchLabelSchema = z.object({
  assetRef: z.string(),
  chassisSerial: z.string().nullable(),
  model: z.string().nullable(),
  band: z.string(),
  wipeResult: z.string(),
  attestation: z.enum(['BENCH', 'SELF']),
  runId: z.string().uuid(),
  qrPayload: z.string().url(),
});

export const benchBindingOutcomeSchema = z.enum([
  'matched',
  'created',
  'customs',
  'collision_pending',
  'foreign_refused',
  'refused',
  'component',
  'no_identity',
]);

export const benchCommitResponseSchema = z.object({
  runId: z.string().uuid(),
  label: benchLabelSchema,
  devices: z.array(
    z.object({
      deviceIndex: z.number().int(),
      outcome: benchBindingOutcomeSchema,
      assetId: z.string().uuid().nullable(),
      appliedBand: z.string().nullable(),
    }),
  ),
});

export type BenchDeviceKind = z.infer<typeof benchDeviceKindSchema>;
export type BenchDevice = z.infer<typeof benchDeviceSchema>;
export type BenchChassis = z.infer<typeof benchChassisSchema>;
export type BenchDiscovery = z.infer<typeof benchDiscoverySchema>;
export type BenchPairStartRequest = z.infer<typeof benchPairStartRequestSchema>;
export type BenchPairStartResponse = z.infer<typeof benchPairStartResponseSchema>;
export type BenchPairPollRequest = z.infer<typeof benchPairPollRequestSchema>;
export type BenchPairPollResponse = z.infer<typeof benchPairPollResponseSchema>;
export type BenchAttestationLevel = z.infer<typeof benchAttestationLevelSchema>;
export type BenchStageStatus = z.infer<typeof benchStageStatusSchema>;
export type BenchProfileStage = z.infer<typeof benchProfileStageSchema>;
export type BenchProfile = z.infer<typeof benchProfileSchema>;
export type BenchStageAck = z.infer<typeof benchStageAckSchema>;
export type BenchStageResult = z.infer<typeof benchStageResultSchema>;
export type BenchRunState = z.infer<typeof benchRunStateSchema>;
export type BenchWipeStartRequest = z.infer<typeof benchWipeStartRequestSchema>;
export type BenchWipeStartResponse = z.infer<typeof benchWipeStartResponseSchema>;
export type BenchEvidenceUrlRequest = z.infer<typeof benchEvidenceUrlRequestSchema>;
export type BenchEvidenceUrlResponse = z.infer<typeof benchEvidenceUrlResponseSchema>;
export type BenchPlanStep = z.infer<typeof benchPlanStepSchema>;
export type BenchPlan = z.infer<typeof benchPlanSchema>;
export type BenchHandoffRequest = z.infer<typeof benchHandoffRequestSchema>;
export type BenchBootTarget = z.infer<typeof benchBootTargetSchema>;
export type BenchResumeGrant = z.infer<typeof benchResumeGrantSchema>;
export type BenchHandoffResponse = z.infer<typeof benchHandoffResponseSchema>;
export type BenchResumeRequest = z.infer<typeof benchResumeRequestSchema>;
export type BenchCompletedStage = z.infer<typeof benchCompletedStageSchema>;
export type BenchRunContinuation = z.infer<typeof benchRunContinuationSchema>;
export type BenchResumeResponse = z.infer<typeof benchResumeResponseSchema>;
export type BenchCommitRequest = z.infer<typeof benchCommitRequestSchema>;
export type BenchLabel = z.infer<typeof benchLabelSchema>;
export type BenchBindingOutcome = z.infer<typeof benchBindingOutcomeSchema>;
export type BenchCommitResponse = z.infer<typeof benchCommitResponseSchema>;
